import { isUndefinedOrEmpty, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { ShippingAddress } from '../../model'
import { useCountry, useInternationalShippingEnabled, useInternationalSupportedShippingCountries, useShippingAddress } from '../../state'
import { isValidAddress } from '../../utils/profile-validation'

export type ShippingAddressDisabledResult = (address: ShippingAddress) => boolean

export const useIsShippingAddressDisabled = (): ShippingAddressDisabledResult => {
  const supportedShippingCountries = useInternationalSupportedShippingCountries()
  const isInternationalShippingEnabled = useInternationalShippingEnabled()
  const consumerCountry = useCountry()

  return (address) => {
    const hasInvalidAddressOrNumber = isUndefinedOrEmpty(address?.phoneNumber) || !isValidAddress(address)
    const isAddressSupported = supportedShippingCountries.includes(address.countryCode)

    if (isInternationalShippingEnabled) {
      return hasInvalidAddressOrNumber || !isAddressSupported
    }
    if (address.countryCode !== consumerCountry) {
      return true
    }
    return hasInvalidAddressOrNumber
  }
}

export const useSortedShippingAddresses = (selectedShippingAddress: ShippingAddress | undefined): ShippingAddress[] => {
  const { allAddresses } = useShippingAddress()
  const supportedShippingCountries = useInternationalSupportedShippingCountries()
  const isInternationalShippingEnabled = useInternationalShippingEnabled()
  const consumerCountry = useCountry()
  const isShippingAddressDisabled = useIsShippingAddressDisabled()

  return allAddresses.slice().sort((a, b) => {
    // sort by selected shipping address as the first item in the list
    const sortByDefault = Number(b.id === selectedShippingAddress?.id) - Number(a.id === selectedShippingAddress?.id || selectedShippingAddress == null)

    // sort by supported country
    const sortInternationalSupported = Number(supportedShippingCountries.includes(b.countryCode)) - Number(supportedShippingCountries.includes(a.countryCode))
    const sortStandard = Number(b.countryCode === consumerCountry) - Number(a.countryCode === consumerCountry)

    const sortByCountryCode = isInternationalShippingEnabled ? sortInternationalSupported : sortStandard

    // sort by preferred
    const sortByPreferred = Number(b.preferred) - Number(a.preferred)

    // sort by enabled status
    const sortByEnabled = Number(isShippingAddressDisabled(a)) - Number(isShippingAddressDisabled(b))

    // sort by phone number defined
    const itemAHasPhoneNumber = !valueIsNotNullOrUndefined(a.phoneNumber) ? 0 : 1
    const itemBHasPhoneNumber = !valueIsNotNullOrUndefined(b.phoneNumber) ? 0 : 1
    const sortPhoneExists = itemBHasPhoneNumber - itemAHasPhoneNumber

    return sortByDefault || sortByCountryCode || sortByEnabled || sortByPreferred || sortPhoneExists
  })
}
